import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { ZoomIn, ZoomOut } from 'lucide-react';
import { apiFetch } from '../../../../shared/apiClient';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import { DynamicsChartFrame } from './DynamicsChartFrame';
import { DYNAMICS_CARD_INSIGHTS } from './dynamicsInsights';
import { DYNAMICS_RECHARTS_TOOLTIP_COMMON } from './dynamicsRechartsTooltip';
import { ResolvedPeriod } from '../../PeriodPicker';
import { linearTrend } from './linearTrend';
import './DynamicsA_PlanDoneDebtVF.css';
import { dynamicsBucketChartMargins, dynamicsPeriodAxisProps } from './dynamicsPeriodAxis';
import { PLAN_DONE_KEYS, PlanDoneDataKey, PLAN_DONE_SERIES_COPY } from './planDoneSeriesMeta';
import { useDynamicsPlotHeight } from './DynamicsChartLayoutContext';

interface Props {
  groupBy: 'sprint' | 'release' | 'week' | 'month';
  lastN: number;
  resolvedPeriod: ResolvedPeriod | null;
  onDataStatus?: (s: { metric: string; hasData: boolean; loading: boolean }) => void;
}

const TREND_STORAGE_KEY = 'dynamics-a-trends';

function clampIndex(i: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, i));
}

const STROKE: Record<PlanDoneDataKey, string> = {
  plan: '#3b82f6',
  done: '#f97316',
  devDebt: '#22c55e',
  qaDebt: '#0ea5e9',
  verificationFailed: '#a855f7',
  devDebtTrend: '#166534',
  qaDebtTrend: '#0369a1',
};

function PlanDoneMetroCue() {
  return (
    <p className="dynamics-a-metro-pop__cue">
      <Link
        to="/settings?tab=project&section=quality-settings"
        className="dynamics-a-metro-pop__link"
      >
        Quality settings →
      </Link>
      <span className="dynamics-a-metro-pop__hint">Edit Jira statuses in Quality settings →</span>
    </p>
  );
}

const MAIN_KEYS: PlanDoneDataKey[] = [
  PLAN_DONE_KEYS.plan,
  PLAN_DONE_KEYS.done,
  PLAN_DONE_KEYS.devDebt,
  PLAN_DONE_KEYS.qaDebt,
  PLAN_DONE_KEYS.verificationFailed,
];

interface QualityStatusLists {
  done_statuses: string[];
  dev_statuses: string[];
  qa_statuses: string[];
  failure_statuses: string[];
}

function PlanDoneMetroStatusChips({ items }: { items: string[] }) {
  if (items.length === 0) {
    return (
      <p className="dynamics-a-metro-pop__empty-statuses">
        No statuses selected yet — open Quality settings and pick statuses for this line.
      </p>
    );
  }
  return (
    <ul className="dynamics-a-metro-status-names">
      {items.map((s) => (
        <li key={s}>{s}</li>
      ))}
    </ul>
  );
}

function PlanDoneMetroPopBody({
  seriesKey,
  lists,
}: {
  seriesKey: PlanDoneDataKey;
  lists: QualityStatusLists | null;
}) {
  const loading = lists === null;

  const isTrend =
    seriesKey === PLAN_DONE_KEYS.devDebtTrend || seriesKey === PLAN_DONE_KEYS.qaDebtTrend;
  if (isTrend) {
    const copy = PLAN_DONE_SERIES_COPY[seriesKey];
    return (
      <ul className="dynamics-a-metro-pop__bullets">
        {copy.bullets.map((b) => (
          <li key={b}>{b}</li>
        ))}
      </ul>
    );
  }

  if (seriesKey === PLAN_DONE_KEYS.plan) {
    return (
      <div className="dynamics-a-metro-pop__body">
        <p className="dynamics-a-metro-pop__lede">
          Issues in this sprint or date-window scope (matches your board sync). Includes{' '}
          <strong>any</strong> Jira status — the line does not filter by Terminal / Dev / QA lists
          below.
        </p>
      </div>
    );
  }

  let label = '';
  let items: string[] = [];
  if (seriesKey === PLAN_DONE_KEYS.done) {
    label = 'Terminal (Done) statuses';
    items = lists?.done_statuses ?? [];
  } else if (seriesKey === PLAN_DONE_KEYS.devDebt) {
    label = 'Dev statuses';
    items = lists?.dev_statuses ?? [];
  } else if (seriesKey === PLAN_DONE_KEYS.qaDebt) {
    label = 'QA statuses';
    items = lists?.qa_statuses ?? [];
  } else if (seriesKey === PLAN_DONE_KEYS.verificationFailed) {
    label = 'Failure statuses (each transition in counts once)';
    items = lists?.failure_statuses ?? [];
  } else {
    return null;
  }

  return (
    <div className="dynamics-a-metro-pop__body">
      {loading ? (
        <p className="dynamics-a-metro-pop__loading">Loading statuses…</p>
      ) : (
        <>
          <p className="dynamics-a-metro-pop__stat-label">{label}</p>
          <PlanDoneMetroStatusChips items={items} />
        </>
      )}
    </div>
  );
}

export const DynamicsA_PlanDoneDebtVF: React.FC<Props> = ({
  groupBy,
  lastN,
  resolvedPeriod,
  onDataStatus,
}) => {
  return (
    <DynamicsChartFrame
      id="A"
      title="Dynamics · Plan vs Done vs Debt vs VF"
      hint="Hover legend labels for the Jira statuses behind each line. Zoom in/out to focus a range, then use the horizontal scrollbar under the chart to move along the timeline."
      needs="One view of plan vs shipped work and where items stack up mid-pipeline—so leaders catch slips before deadlines, not in the retro. Hook up Jira (board + synced issues); for sprints we align each column with sprint dates and moves that touch that window."
      metric="plan-done-debt-vf"
      groupBy={groupBy}
      lastN={lastN}
      resolvedPeriod={resolvedPeriod}
      onDataStatus={onDataStatus}
      insightsTitle={DYNAMICS_CARD_INSIGHTS.A.title}
      insights={[...DYNAMICS_CARD_INSIGHTS.A.bullets]}
    >
      {(points) => <InnerChart points={points as ChartPoint[]} />}
    </DynamicsChartFrame>
  );
};

interface ChartPoint {
  period: string;
  plan?: number;
  done?: number;
  devDebt?: number;
  qaDebt?: number;
  verificationFailed?: number;
  [k: string]: number | string | null | undefined;
}

const PlanDoneMetroLegend: React.FC<{
  keys: PlanDoneDataKey[];
  statusLists: QualityStatusLists | null;
}> = ({ keys, statusLists }) => {
  const uid = useId();
  return (
    <ul className="dynamics-a-metro-legend" aria-label="Chart series">
      {keys.map((key) => {
        const copy = PLAN_DONE_SERIES_COPY[key];
        const stroke = STROKE[key];
        const isDashed = key === PLAN_DONE_KEYS.verificationFailed;
        const isThin = key === PLAN_DONE_KEYS.devDebtTrend || key === PLAN_DONE_KEYS.qaDebtTrend;
        const popId = `${uid}-pop-${key}`;
        return (
          <li key={key} className="dynamics-a-metro-legend__cell">
            <button
              type="button"
              className="dynamics-a-metro-legend__trigger"
              aria-describedby={popId}
            >
              <span
                className="dynamics-a-metro-legend__dot"
                style={{
                  width: 14,
                  height: !isThin && !isDashed ? 8 : isDashed ? 0 : 3,
                  background: !isThin && !isDashed ? stroke : 'transparent',
                  borderBottom: isDashed ? `3px dashed ${stroke}` : undefined,
                  borderRadius: !isThin && !isDashed ? 2 : 0,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  backgroundSize: isThin ? '100% 2px' : undefined,
                  backgroundImage: isThin ? `linear-gradient(${stroke}, ${stroke})` : undefined,
                  minHeight: isThin ? 3 : undefined,
                  minWidth: 14,
                }}
                aria-hidden
              />
              {copy.legend}
            </button>
            <div className="dynamics-a-metro-pop" id={popId} role="tooltip">
              <p className="dynamics-a-metro-pop__title">{copy.legend}</p>
              <PlanDoneMetroPopBody seriesKey={key} lists={statusLists} />
              <PlanDoneMetroCue />
            </div>
          </li>
        );
      })}
    </ul>
  );
};

const PlanDoneTooltipContent: React.FC<{
  active?: boolean;
  label?: string;
  payload?: readonly { dataKey?: unknown; value?: number; color?: string }[];
  showTrends: boolean;
}> = ({ active, label, payload, showTrends }) => {
  if (!active || !payload?.length) return null;
  const rows = payload.filter((p) => {
    const k = String(p.dataKey ?? '') as PlanDoneDataKey;
    if (!PLAN_DONE_SERIES_COPY[k]) return false;
    if (!showTrends && (k === 'devDebtTrend' || k === 'qaDebtTrend')) return false;
    return true;
  });
  return (
    <div className="dynamics-a-chart-tooltip">
      <div className="dynamics-a-chart-tooltip__bucket">{label}</div>
      {rows.map((p) => {
        const k = String(p.dataKey ?? '') as PlanDoneDataKey;
        const copy = PLAN_DONE_SERIES_COPY[k];
        const v = p.value;
        return (
          <div key={k} className="dynamics-a-chart-tooltip__row">
            <span className="dynamics-a-chart-tooltip__name">{copy.legend}</span>
            <span className="dynamics-a-chart-tooltip__val">
              {typeof v === 'number' && Number.isFinite(v) ? Math.round(v * 100) / 100 : '—'}
            </span>
          </div>
        );
      })}
      <p className="dynamics-a-chart-tooltip__hint">
        Hover legend labels for the exact Jira statuses. Failed checks = transitions into your
        failure list; Done / In Dev / In QA use snapshot statuses from Quality settings.
      </p>
    </div>
  );
};

const InnerChart: React.FC<{
  points: ChartPoint[];
}> = ({ points }) => {
  const chartPlotHeight = useDynamicsPlotHeight(360);

  const [showTrends, setShowTrends] = useState(() => {
    try {
      return localStorage.getItem(TREND_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(TREND_STORAGE_KEY, showTrends ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [showTrends]);

  const [qualityStatusLists, setQualityStatusLists] = useState<QualityStatusLists | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/quality/settings');
        if (!res.ok) throw new Error('settings');
        const data = (await res.json()) as { settings: QualityStatusLists };
        if (!cancelled) setQualityStatusLists(data.settings);
      } catch {
        if (!cancelled) {
          setQualityStatusLists({
            done_statuses: [],
            dev_statuses: [],
            qa_statuses: [],
            failure_statuses: [],
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const data = useMemo(() => {
    const devDebtTrend = linearTrend(
      points.map((p) => (typeof p.devDebt === 'number' ? p.devDebt : null))
    );
    const qaDebtTrend = linearTrend(
      points.map((p) => (typeof p.qaDebt === 'number' ? p.qaDebt : null))
    );
    return points.map((p, i) => ({
      ...p,
      devDebtTrend: devDebtTrend[i],
      qaDebtTrend: qaDebtTrend[i],
    }));
  }, [points]);

  /** User-controlled window on full period list (`null` = show all). */
  const [brushManual, setBrushManual] = useState<{ start: number; end: number } | null>(null);

  useEffect(() => {
    setBrushManual(null);
  }, [data.length]);

  const brush = useMemo(() => {
    const n = data.length;
    if (!n) return { start: 0, end: 0 };
    const full = { start: 0, end: n - 1 };
    if (!brushManual) return full;
    const s = clampIndex(brushManual.start, 0, n - 1);
    const e = clampIndex(brushManual.end, s, n - 1);
    return { start: s, end: e };
  }, [data.length, brushManual]);

  const visibleData = useMemo(
    () => (data.length ? data.slice(brush.start, brush.end + 1) : []),
    [data, brush]
  );

  const viewportRef = useRef<HTMLDivElement>(null);
  const [hostWidth, setHostWidth] = useState(0);
  const dataLenRef = useRef(data.length);
  dataLenRef.current = data.length;

  /** Keep latest brush for scroll handler without resubscribing. */
  const brushRef = useRef(brush);
  brushRef.current = brush;

  const ignoreScrollSyncRef = useRef(false);

  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && Number.isFinite(w)) {
        setHostWidth(Math.max(0, Math.floor(w)));
      }
    });
    ro.observe(el);
    setHostWidth(Math.max(0, Math.floor(el.getBoundingClientRect().width)));
    return () => ro.disconnect();
  }, []);

  /** Wider scroll track while zoomed in so the scrollbar maps to the hidden buckets on the timeline. */
  const innerTrackWidthPx = useMemo(() => {
    const n = data.length;
    if (!n) return Math.max(hostWidth || 560, 320);
    const spanVisible = brush.end - brush.start + 1;
    const base = Math.max(hostWidth || 320, 280);
    if (spanVisible >= n) return base;
    return Math.max(base, Math.ceil((base * n) / Math.max(spanVisible, 1)));
  }, [data.length, hostWidth, brush.start, brush.end]);

  /** After zoom/layout, scrollbar position mirrors the brush window along the virtual full timeline. */
  useLayoutEffect(() => {
    const host = viewportRef.current;
    if (!host) return;
    const n = data.length;
    if (n <= 1) return;
    const b = brush;
    const w = b.end - b.start + 1;

    const apply = () => {
      ignoreScrollSyncRef.current = true;
      try {
        if (w >= n) {
          host.scrollLeft = 0;
          return;
        }
        const maxScroll = host.scrollWidth - host.clientWidth;
        if (maxScroll <= 0) return;
        const maxStart = Math.max(0, n - w);
        const frac = maxStart <= 0 ? 0 : b.start / maxStart;
        host.scrollLeft = frac * maxScroll;
      } finally {
        queueMicrotask(() => {
          ignoreScrollSyncRef.current = false;
        });
      }
    };

    apply();
    const id = requestAnimationFrame(apply);
    return () => cancelAnimationFrame(id);
  }, [data.length, brush.start, brush.end, innerTrackWidthPx, hostWidth]);

  const handleHorizontalScroll = useCallback(() => {
    if (ignoreScrollSyncRef.current) return;
    const host = viewportRef.current;
    if (!host) return;
    const n = dataLenRef.current;
    if (n <= 1) return;
    const b = brushRef.current;
    const span = b.end - b.start + 1;
    if (span >= n) return;

    const maxScroll = host.scrollWidth - host.clientWidth;
    if (maxScroll <= 0) return;
    const maxStart = Math.max(0, n - span);
    const frac = maxScroll <= 0 ? 0 : host.scrollLeft / maxScroll;
    const newStart = clampIndex(Math.round(frac * maxStart), 0, maxStart);
    if (newStart === b.start) return;
    setBrushManual({ start: newStart, end: newStart + span - 1 });
  }, []);

  const zoomInRange = useCallback(() => {
    const n = data.length;
    if (n <= 2) return;
    const { start: lo, end: hi } = brush;
    const w = hi - lo + 1;
    if (w <= 2) return;
    const newW = Math.max(2, Math.floor(w * 0.62));
    const mid = Math.floor((lo + hi) / 2);
    let ns = mid - Math.floor(newW / 2);
    let ne = ns + newW - 1;
    if (ns < 0) {
      ns = 0;
      ne = ns + newW - 1;
    }
    if (ne >= n) {
      ne = n - 1;
      ns = Math.max(0, ne - newW + 1);
    }
    setBrushManual({ start: ns, end: ne });
  }, [data.length, brush]);

  const zoomOutRange = useCallback(() => {
    const n = data.length;
    if (!n) return;
    const { start: lo, end: hi } = brush;
    const w = hi - lo + 1;
    const newW = Math.min(n, Math.max(w + 1, Math.ceil(w * 1.45)));
    const mid = Math.floor((lo + hi) / 2);
    let ns = mid - Math.floor(newW / 2);
    let ne = ns + newW - 1;
    if (ns < 0) {
      ns = 0;
      ne = Math.min(n - 1, newW - 1);
    }
    if (ne >= n) {
      ne = n - 1;
      ns = Math.max(0, ne - newW + 1);
    }
    if (newW >= n || (ns === 0 && ne === n - 1)) {
      setBrushManual(null);
      return;
    }
    setBrushManual({ start: clampIndex(ns, 0, n - 1), end: clampIndex(ne, 0, n - 1) });
  }, [data.length, brush]);

  const spansFullTimeline =
    data.length <= 1 || (brush.start === 0 && brush.end === data.length - 1);

  const legendKeys = useMemo(
    () =>
      showTrends
        ? [...MAIN_KEYS, PLAN_DONE_KEYS.devDebtTrend, PLAN_DONE_KEYS.qaDebtTrend]
        : MAIN_KEYS,
    [showTrends]
  );

  const tooltipRender = useCallback(
    (props: TooltipProps<number, string>) => (
      <PlanDoneTooltipContent
        active={props.active}
        label={props.label as string | undefined}
        payload={props.payload as readonly { dataKey?: unknown; value?: number }[] | undefined}
        showTrends={showTrends}
      />
    ),
    [showTrends]
  );

  return (
    <div className="dynamics-a-wrap">
      <div className="dynamics-a-trends-bar dynamics-a-zoom-toolbar">
        <label className="dynamics-a-trends-bar__trend">
          <input
            type="checkbox"
            checked={showTrends}
            onChange={(e) => setShowTrends(e.target.checked)}
          />
          Trend lines
        </label>
        {data.length > 1 ? (
          <>
            <div className="dynamics-a-zoom-toolbar__buttons" aria-label="Chart zoom">
              <button
                type="button"
                className="dynamics-a-zoom-toolbar__btn"
                onClick={zoomInRange}
                disabled={!visibleData.length || brush.end - brush.start + 1 <= 2}
                title="Zoom in — narrower period window"
              >
                <ZoomIn size={14} aria-hidden /> Zoom in
              </button>
              <button
                type="button"
                className="dynamics-a-zoom-toolbar__btn"
                onClick={zoomOutRange}
                disabled={spansFullTimeline}
                title="Zoom out — wider period window"
              >
                <ZoomOut size={14} aria-hidden /> Zoom out
              </button>
            </div>
            <span className="dynamics-a-pan-hint">
              Zoom in, then slide the scrollbar under the chart to move the window along the full
              timeline.
            </span>
          </>
        ) : null}
      </div>
      <PlanDoneMetroLegend keys={legendKeys} statusLists={qualityStatusLists} />
      <div ref={viewportRef} className="dynamics-a-chart-hscroll" onScroll={handleHorizontalScroll}>
        <div
          className="dynamics-a-chart-hscroll-inner"
          style={{ width: innerTrackWidthPx, minWidth: innerTrackWidthPx }}
        >
          <ResponsiveContainer width="100%" height={chartPlotHeight}>
            <LineChart data={visibleData} margin={{ ...dynamicsBucketChartMargins, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
              <XAxis {...dynamicsPeriodAxisProps} />
              <YAxis />
              <Tooltip content={tooltipRender} {...DYNAMICS_RECHARTS_TOOLTIP_COMMON} />
              <Line
                type="monotone"
                dataKey="plan"
                name={PLAN_DONE_SERIES_COPY.plan.legend}
                stroke={STROKE.plan}
                strokeWidth={2}
                dot={{ r: 4 }}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="done"
                name={PLAN_DONE_SERIES_COPY.done.legend}
                stroke={STROKE.done}
                strokeWidth={2}
                dot={{ r: 4 }}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="devDebt"
                name={PLAN_DONE_SERIES_COPY.devDebt.legend}
                stroke={STROKE.devDebt}
                strokeWidth={2}
                dot={{ r: 4 }}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="qaDebt"
                name={PLAN_DONE_SERIES_COPY.qaDebt.legend}
                stroke={STROKE.qaDebt}
                strokeWidth={2}
                dot={{ r: 4 }}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="verificationFailed"
                name={PLAN_DONE_SERIES_COPY.verificationFailed.legend}
                stroke={STROKE.verificationFailed}
                strokeWidth={2}
                strokeDasharray="4 4"
                dot={{ r: 3 }}
                isAnimationActive={false}
              />
              {showTrends && (
                <>
                  <Line
                    type="linear"
                    dataKey="devDebtTrend"
                    name={PLAN_DONE_SERIES_COPY.devDebtTrend.legend}
                    stroke={STROKE.devDebtTrend}
                    strokeWidth={1}
                    dot={false}
                    isAnimationActive={false}
                  />
                  <Line
                    type="linear"
                    dataKey="qaDebtTrend"
                    name={PLAN_DONE_SERIES_COPY.qaDebtTrend.legend}
                    stroke={STROKE.qaDebtTrend}
                    strokeWidth={1}
                    dot={false}
                    isAnimationActive={false}
                  />
                </>
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
